import { URL_QUERY_SKIP_LOGIN_TOKEN } from 'constant/routes'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { selectTokenInfo } from 'redux/selector/user'
import { useNavigateTo } from './useNavigateTo'
import { useWebViewHandler } from './useWebViewHandler'

interface OpenGamePopupParams {
  id: number
  gameType?: number | string
  tpCode?: string
  direction?: number
}

interface GamePopupInfo {
  gameId: number
  gameType: number | string
  tpCode: string
  direction: number
  window: Window | null
}

interface GamePopupContextValue {
  openGamePopup: (params: OpenGamePopupParams) => void
  closeGamePopup: () => void
  toDepositFromGame: () => void
}

const GamePopupContext = createContext<GamePopupContextValue>({
  openGamePopup: ({ id, gameType, tpCode, direction }) => {},
  closeGamePopup: () => {},
  toDepositFromGame: () => {},
})

interface GamePopupProviderProps {
  children: React.ReactNode
}

export const GamePopupProvider = ({ children }: GamePopupProviderProps): React.ReactElement => {
  const currentPopup = useRef<GamePopupInfo | null | undefined>(null)
  const tokenInfo = useSelector(selectTokenInfo)
  const { navigateToGameDeposit } = useNavigateTo()
  const { checkInWebview, requestOpenGame, requestCloseGame } = useWebViewHandler()

  useEffect(() => {
    const listener = (ev: MessageEvent) => {
      if (ev.data?.source === 'game-now' && currentPopup.current) {
        currentPopup.current.window!.close()

        if (ev.data?.type === 'deposit') {
          navigateToGameDeposit({
            fromGame: currentPopup.current.gameId,
            fromGameTpCode: currentPopup.current.tpCode,
            fromGameType: currentPopup.current.gameType as number,
            fromGameDirection: currentPopup.current.direction,
          })
        }
      }
    }
    window.addEventListener('message', listener)
    return () => {
      window.removeEventListener('message', listener)
    }
  }, [navigateToGameDeposit])

  const toDepositFromGame = useCallback(() => {
    const gameInfo = currentPopup.current
    if (!gameInfo) return
    navigateToGameDeposit({
      fromGame: gameInfo.gameId,
      fromGameTpCode: gameInfo.tpCode,
      fromGameType: gameInfo.gameType as number,
      fromGameDirection: gameInfo.direction,
    })
  }, [navigateToGameDeposit])

  const openGamePopup = useCallback(
    ({ id, gameType = '', tpCode = '', direction = 0 }: OpenGamePopupParams) => {
      const url = `/game-now?id=${id}&gameType=${gameType ?? ''}&tpCode=${tpCode ?? ''}${
        direction > 0 && '&direction=' + direction
      }&${URL_QUERY_SKIP_LOGIN_TOKEN}=${encodeURIComponent(JSON.stringify(tokenInfo))}`
      if (checkInWebview()) {
        requestOpenGame(url)
        currentPopup.current = {
          gameId: id,
          gameType,
          tpCode,
          direction,
          window: null,
        }
      } else {
        if (currentPopup.current) {
          try {
            currentPopup.current?.window?.close()
          } catch (err) {}
          currentPopup.current = null
        }

        const newPopup = window.open(url, '_blank')!
        newPopup.focus()
        currentPopup.current = {
          gameId: id,
          gameType,
          tpCode,
          direction,
          window: newPopup,
        }
      }
    },
    [checkInWebview, requestOpenGame, tokenInfo]
  )

  const closeGamePopup = useCallback(() => {
    if (checkInWebview()) {
      requestCloseGame()
    }
    if (currentPopup.current) {
      currentPopup.current.window?.close()
      currentPopup.current = undefined
    }
  }, [checkInWebview, requestCloseGame])

  return useMemo(
    () => (
      <GamePopupContext.Provider value={{ openGamePopup, closeGamePopup, toDepositFromGame }}>
        {children}
      </GamePopupContext.Provider>
    ),
    [children, closeGamePopup, openGamePopup, toDepositFromGame]
  )
}

export const useOpenGamePopup = (): ((params: OpenGamePopupParams) => void) => {
  const ctx = useContext(GamePopupContext)
  return ctx.openGamePopup
}

export const useCloseGamePopup = (): (() => void) => {
  const ctx = useContext(GamePopupContext)
  return ctx.closeGamePopup
}

export const useToDepositFromGame = (): (() => void) => {
  const ctx = useContext(GamePopupContext)
  return ctx.toDepositFromGame
}
